import { HubConnectionState } from '@microsoft/signalr';
import { OfflineLevelEnum } from 'src/enums/offlineLevelEnum';

export type ConnectionSyncState = 'pending' | 'resyncing' | 'current' | 'failed' | 'blocked-contract';

export interface ConnectionStatusModelInput {
    offlineLevel: OfflineLevelEnum;
    connectionStatus: HubConnectionState;
    selectedVesselImoNumber: number | undefined;
    connectedVesselGroup: number | null;
    syncState: ConnectionSyncState;
}

export interface ConnectionStatusPresentation {
    label: string;
    detail: string;
    icon: 'wifi' | 'rotate' | 'triangle-exclamation' | 'link';
    tone: 'success' | 'warning' | 'error' | 'neutral';
    canRecover: boolean;
    actionLabel?: string;
}

/**
 * Gives API reachability precedence over transport details so the header never
 * implies that operational data is current while the API is unavailable.
 */
export const getConnectionStatusPresentation = (input: ConnectionStatusModelInput): ConnectionStatusPresentation => {
    if (input.offlineLevel === OfflineLevelEnum.CheckingConnection) {
        return {
            label: 'Connecting',
            detail: 'Checking connection to the server.',
            icon: 'rotate',
            tone: 'neutral',
            canRecover: false,
        };
    }

    if (input.offlineLevel === OfflineLevelEnum.Offline) {
        return {
            label: 'Working offline',
            detail: 'Unable to connect to the server. Check your network connection.',
            icon: 'triangle-exclamation',
            tone: 'error',
            canRecover: false,
        };
    }

    if (input.offlineLevel === OfflineLevelEnum.BadConnectionDetected) {
        return {
            label: 'Slow connection',
            detail: 'Server responses are delayed. Live updates may take longer to appear.',
            icon: 'triangle-exclamation',
            tone: 'warning',
            canRecover: false,
        };
    }

    if (!input.selectedVesselImoNumber) {
        return {
            label: 'Waiting for vessel',
            detail: 'Select a vessel to receive live updates.',
            icon: 'link',
            tone: 'neutral',
            canRecover: false,
        };
    }

    if (input.connectionStatus === HubConnectionState.Reconnecting || input.connectionStatus === HubConnectionState.Connecting) {
        return {
            label: 'Reconnecting to live updates',
            detail: 'Connection temporarily interrupted. OrcaWeb will reconnect automatically.',
            icon: 'rotate',
            tone: 'warning',
            canRecover: false,
        };
    }

    if (input.connectionStatus !== HubConnectionState.Connected) {
        return {
            label: 'Live updates paused',
            detail: 'You are not receiving live updates for this vessel. Saved work is not lost.',
            icon: 'triangle-exclamation',
            tone: 'error',
            canRecover: true,
            actionLabel: 'Reconnect',
        };
    }

    if (input.connectedVesselGroup !== input.selectedVesselImoNumber) {
        return {
            label: 'Joining vessel updates',
            detail: 'Connecting to live updates for this vessel.',
            icon: 'rotate',
            tone: 'warning',
            canRecover: false,
        };
    }

    if (input.syncState === 'resyncing') {
        return {
            label: 'Syncing vessel data',
            detail: 'Updating vessel data to the latest version.',
            icon: 'rotate',
            tone: 'warning',
            canRecover: false,
        };
    }

    if (input.syncState === 'current') {
        return {
            label: 'Connected',
            detail: 'OrcaWeb is receiving live updates for this vessel.',
            icon: 'wifi',
            tone: 'success',
            canRecover: false,
        };
    }

    if (input.syncState === 'failed') {
        return {
            label: 'Sync failed',
            detail: 'Connection is open, but the latest vessel data could not be refreshed.',
            icon: 'triangle-exclamation',
            tone: 'error',
            canRecover: true,
            actionLabel: 'Retry sync',
        };
    }

    if (input.syncState === 'blocked-contract') {
        return {
            label: 'Connected',
            detail: 'OrcaWeb is receiving live updates for this vessel.',
            icon: 'wifi',
            tone: 'success',
            canRecover: false,
        };
    }

    return {
        label: 'Syncing vessel data',
        detail: 'Updating vessel data to the latest version.',
        icon: 'rotate',
        tone: 'warning',
        canRecover: false,
    };
};
